import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import DateInput from '@/components/ui/date-input';
import useListKeyboardNav, { LIST_ROW_CLASS } from '@/hooks/useListKeyboardNav';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const EMPTY = {
  name: '',
  valid_from: new Date().toISOString().slice(0, 10),
  valid_until: '',
  notes: '',
  affects_billing: true,
  is_active: true,
};

function fmtDate(d) {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  if (!y || !m || !day) return d;
  return `${day}.${m}.${y}`;
}

export default function CancellationPeriodsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const openPeriodEdit = (row) => setEditor({
    ...row,
    valid_from: row.valid_from || '',
    valid_until: row.valid_until || '',
    notes: row.notes || '',
  });

  const { getRowProps } = useListKeyboardNav({
    items: rows,
    onSelect: openPeriodEdit,
    enabled: !editor && !toDelete,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/tariffs-discounts/cancellation-periods');
      setRows(r.data || []);
    } catch {
      toast.error('Ausfallperioden konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editor.name?.trim()) { toast.error('Name ist erforderlich'); return; }
    if (!editor.valid_from) { toast.error('Beginn ist erforderlich'); return; }
    const payload = {
      name: editor.name.trim(),
      valid_from: editor.valid_from,
      valid_until: editor.valid_until || null,
      notes: editor.notes?.trim() || null,
      affects_billing: !!editor.affects_billing,
      is_active: !!editor.is_active,
    };
    try {
      if (editor.id) {
        await api.patch(`/tariffs-discounts/cancellation-periods/${editor.id}`, payload);
        toast.success('Ausfallperiode aktualisiert');
      } else {
        await api.post('/tariffs-discounts/cancellation-periods', payload);
        toast.success('Ausfallperiode angelegt');
      }
      setEditor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/tariffs-discounts/cancellation-periods/${toDelete.id}`);
      toast.success('Ausfallperiode gelöscht');
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-3" data-testid="cancellation-periods-tab">
      <p className="text-sm text-slate-600">
        Zeiträume für Unterrichtsausfälle / gebührenrelevante Unterbrechungen (Stammdaten — noch ohne Abrechnungs-Anbindung).
      </p>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Ausfallperioden</h2>
          <Button size="sm" variant="outline" onClick={() => setEditor({ ...EMPTY })} data-testid="add-period-btn">
            <Plus className="w-4 h-4 mr-1" /> Neue Periode
          </Button>
        </div>
        {loading ? (
          <div className="p-10 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-red" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 font-semibold">Bezeichnung</th>
                  <th className="px-3 py-2 font-semibold">von</th>
                  <th className="px-3 py-2 font-semibold">bis</th>
                  <th className="px-3 py-2 font-semibold text-center">Abrechnung</th>
                  <th className="px-3 py-2 font-semibold">Notiz</th>
                  <th className="px-3 py-2 font-semibold w-20" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-slate-400">Noch keine Ausfallperioden</td>
                  </tr>
                )}
                {rows.map((row, idx) => (
                  <tr
                    key={row.id}
                    className={LIST_ROW_CLASS}
                    data-testid={`period-row-${row.id}`}
                    {...getRowProps(idx, row)}
                  >
                    <td className="px-3 py-2 font-medium">
                      {row.name}
                      {!row.is_active && <span className="ml-1.5 text-[10px] text-slate-400">inaktiv</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(row.valid_from)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(row.valid_until)}</td>
                    <td className="px-3 py-2 text-center text-xs">
                      {row.affects_billing ? 'berücksichtigen' : 'ignorieren'}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[220px]">{row.notes || ''}</td>
                    <td className="px-3 py-2">
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => setToDelete(row)} title="Löschen">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!editor} onOpenChange={(o) => { if (!o) setEditor(null); }}>
        <DialogContent data-testid="period-editor">
          <DialogHeader>
            <DialogTitle>{editor?.id ? 'Ausfallperiode bearbeiten' : 'Neue Ausfallperiode'}</DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="space-y-3 mt-1">
              <div>
                <Label>Bezeichnung *</Label>
                <Input value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} data-testid="period-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>von *</Label>
                  <DateInput value={editor.valid_from || ''} onChange={(e) => setEditor({ ...editor, valid_from: e.target.value })} />
                </div>
                <div>
                  <Label>bis</Label>
                  <DateInput value={editor.valid_until || ''} onChange={(e) => setEditor({ ...editor, valid_until: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Notiz</Label>
                <Textarea rows={2} value={editor.notes || ''} onChange={(e) => setEditor({ ...editor, notes: e.target.value })} />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch checked={!!editor.affects_billing} onCheckedChange={(v) => setEditor({ ...editor, affects_billing: v })} />
                  Für Abrechnung berücksichtigen
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch checked={!!editor.is_active} onCheckedChange={(v) => setEditor({ ...editor, is_active: v })} />
                  Aktiv
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)}>Abbrechen</Button>
            <Button onClick={save} data-testid="save-period-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ausfallperiode löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{toDelete?.name}</strong> wird entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
